import { Paper, Text, Group, ActionIcon } from "@mantine/core";
import { IconFile, IconX } from "@tabler/icons-react";
import { formatFileSize } from "../utils/utilsFunctions/functions";
import { useEffect, useRef } from "react";

interface AttachmentsPreviewPanelProps {
  attachments: File[];
  pdfUrls: Map<string, string>;
  onRemoveAttachment: (index: number) => void;
}

export const AttachmentsPreviewPanel = ({
  attachments,
  pdfUrls,
  onRemoveAttachment,
}: AttachmentsPreviewPanelProps) => {
  const previewBlobUrlsRef = useRef<Map<number, string>>(new Map());

  useEffect(() => {
    const blobUrls = new Map<number, string>();

    attachments.forEach((file, index) => {
      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");
      if (isPdf) {
        const blobUrl = URL.createObjectURL(file);
        blobUrls.set(index, blobUrl);
      }
    });

    // Revoke old blob URLs that are no longer needed
    previewBlobUrlsRef.current.forEach((url, index) => {
      if (!blobUrls.has(index)) {
        URL.revokeObjectURL(url);
      }
    });

    previewBlobUrlsRef.current = blobUrls;

    return () => {
      // Cleanup: revoke all blob URLs on unmount
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [attachments]);

  if (attachments.length === 0) {
    return null;
  }

  return (
    <div className="mt-3">
      <Paper p="md" withBorder shadow="sm">
        <Text size="sm" fw={600} mb="md">
          Attachments ({attachments.length})
        </Text>
        <div
          className="grid gap-3 grid-cols-2 max-h-96 overflow-y-auto pr-1"
          style={{ gridAutoRows: "1fr" }}
        >
          {attachments.map((file, index) => {
            const isPdf =
              file.type === "application/pdf" ||
              file.name.toLowerCase().endsWith(".pdf");
            const previewUrl = previewBlobUrlsRef.current.get(index);

            return (
              <Paper
                key={`${file.name}-${index}`}
                p="xs"
                withBorder
                className="max-w-xl relative flex flex-col gap-2 h-full"
              >
                <div className="flex items-start gap-2 pr-6">
                  <IconFile size={16} className="mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Text size="xs" truncate="end" fw={500}>
                      {file.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatFileSize(file.size)}
                    </Text>
                  </div>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemoveAttachment(index)}
                    aria-label="Remove attachment"
                    className="absolute top-1 right-1"
                  >
                    <IconX size={16} />
                  </ActionIcon>
                </div>
                {isPdf && (
                  <a
                    href={previewUrl || URL.createObjectURL(file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full border rounded overflow-hidden bg-white block cursor-pointer"
                    style={{ height: "200px", position: "relative" }}
                    onClick={(e) => {
                      // Ensure we have a valid URL - create one on click if needed
                      if (!previewUrl) {
                        const fallbackUrl = URL.createObjectURL(file);
                        e.currentTarget.href = fallbackUrl;
                      }
                    }}
                  >
                    <iframe
                      src={`${previewUrl || URL.createObjectURL(file)}#toolbar=0`}
                      className="w-full h-full pointer-events-none"
                      style={{
                        border: "none",
                        backgroundColor: "white",
                      }}
                      title={`PDF preview: ${file.name}`}
                    />
                  </a>
                )}
              </Paper>
            );
          })}
        </div>
      </Paper>
    </div>
  );
};
